"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrencies } from "@/features/currencies/hooks/useCurrencies";
import { useDebounce } from "@/hooks/useDebounce";
import { formatTokenSymbol } from "@/lib/asset-naming";
import { useAuthStore } from "@/store/useAuthStore";
import { getAuctions } from "../../api";
import type { AuctionItem, AuctionModelType } from "../../types";
import { toFilterDate } from "./filterDate";

/* eslint-disable @typescript-eslint/no-explicit-any -- the currencies payload is untyped, as it was in MyAuctionsClient */

/** Twelve at a time — the legacy page size, now a page per scroll step. */
export const MY_AUCTIONS_PAGE = 12;

/**
 * THE OWNER'S AUCTIONS — MyAuctionsClient's rules, moved here unchanged:
 * `/auctions?user_id=<me>` with the title (debounced 400ms), kind, currency
 * and the two dates; asked for only once the viewer is known. Any change of
 * filter starts over at the first page (the legacy `setPage(1)`), because
 * the filters are the query's key. The one difference is the paging: the
 * next twelve load as the owner scrolls instead of behind numbered pages.
 *
 * The status figures count what is loaded, as the legacy cards counted the
 * page on screen — `/auctions` has no status filter and returns no breakdown;
 * only the total is the server's own.
 */
export function useMyAuctions(lang) {
  const $ = _c(56);
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [modelType, setModelType] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const debouncedTitle = useDebounce(title, 400);
  const {
    user
  } = useAuthStore();
  const userId = user?.id;
  const {
    data: currenciesData
  } = useCurrencies();
  const t0 = debouncedTitle || undefined;
  const t1 = modelType as AuctionModelType || undefined;
  const t2 = currencyId ? Number(currencyId) : undefined;
  let t3;
  if ($[0] !== fromDate) {
    t3 = toFilterDate(fromDate);
    $[0] = fromDate;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== toDate) {
    t4 = toFilterDate(toDate);
    $[2] = toDate;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== lang || $[5] !== t0 || $[6] !== t1 || $[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== userId) {
    t5 = {
      user_id: userId,
      title: t0,
      model_type: t1,
      currency_id: t2,
      from: t3,
      to: t4,
      limit: MY_AUCTIONS_PAGE,
      lang
    };
    $[4] = lang;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = userId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const params = t5;
  let t6;
  if ($[12] !== params) {
    t6 = ["auctions", "mine", params];
    $[12] = params;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== params) {
    t7 = t8 => {
      const {
        pageParam
      } = t8;
      return getAuctions({
        ...params,
        page: pageParam
      });
    };
    $[14] = params;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = Boolean(userId);
  let t9;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = {
      queryKey: t6,
      initialPageParam: 1,
      queryFn: t7,
      getNextPageParam: _temp,
      enabled: t8,
      staleTime: 60000
    };
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const query = useInfiniteQuery(t9);
  let out;
  if ($[20] !== query.data?.pages) {
    const seen = new Set();
    out = [];
    for (const p of query.data?.pages ?? []) {
      for (const a of p.data.items) {
        if (!seen.has(a.id)) {
          seen.add(a.id);
          out.push(a);
        }
      }
    }
    $[20] = query.data?.pages;
    $[21] = out;
  } else {
    out = $[21];
  }
  const auctions = out;
  const total = query.data?.pages[0]?.data.page.total ?? auctions.length;
  let t10;
  if ($[22] !== auctions) {
    t10 = auctions.reduce(_temp2, {} as Record<string, number>);
    $[22] = auctions;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const statusCounts = t10;
  let t11;
  if ($[24] !== currenciesData?.data?.currencies) {
    t11 = currenciesData?.data?.currencies ?? [];
    $[24] = currenciesData?.data?.currencies;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = t11.map(_temp3);
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const currencies = t12 as {
    key: string;
    label: string;
  }[];
  let t13;
  if ($[28] !== query || $[29] !== queryClient) {
    t13 = async () => {
      await queryClient.invalidateQueries({
        queryKey: ["auctions"]
      });
      await query.refetch();
    };
    $[28] = query;
    $[29] = queryClient;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const refresh = t13;
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setTitle("");
      setModelType("");
      setCurrencyId("");
      setFromDate("");
      setToDate("");
    };
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const reset = t14;
  const filtered = Boolean(title || modelType || currencyId || fromDate || toDate);
  let t15;
  if ($[32] !== currencyId || $[33] !== filtered || $[34] !== fromDate || $[35] !== modelType || $[36] !== title || $[37] !== toDate) {
    t15 = {
      title,
      setTitle,
      modelType,
      setModelType,
      currencyId,
      setCurrencyId,
      fromDate,
      setFromDate,
      toDate,
      setToDate,
      reset,
      filtered
    };
    $[32] = currencyId;
    $[33] = filtered;
    $[34] = fromDate;
    $[35] = modelType;
    $[36] = title;
    $[37] = toDate;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const t16 = !userId || query.isPending;
  const t17 = query.isError && auctions.length === 0;
  const t18 = query.isFetching && !query.isFetchingNextPage && !query.isPending;
  const t19 = Boolean(query.hasNextPage);
  let t20;
  let t21;
  if ($[39] !== query) {
    t20 = () => {
      query.fetchNextPage();
    };
    t21 = () => {
      query.refetch();
    };
    $[39] = query;
    $[40] = t20;
    $[41] = t21;
  } else {
    t20 = $[40];
    t21 = $[41];
  }
  let t22;
  if ($[42] !== auctions || $[43] !== currencies || $[44] !== query.isFetchingNextPage || $[45] !== refresh || $[46] !== statusCounts || $[47] !== t15 || $[48] !== t16 || $[49] !== t17 || $[50] !== t18 || $[51] !== t19 || $[52] !== t20 || $[53] !== t21 || $[54] !== total) {
    t22 = {
      filters: t15,
      currencies,
      auctions,
      total,
      statusCounts,
      isLoading: t16,
      isError: t17,
      isRefreshing: t18,
      canFetchMore: t19,
      isFetchingMore: query.isFetchingNextPage,
      fetchMore: t20,
      retry: t21,
      refresh
    };
    $[42] = auctions;
    $[43] = currencies;
    $[44] = query.isFetchingNextPage;
    $[45] = refresh;
    $[46] = statusCounts;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
    $[54] = total;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  return t22;
}
function _temp3(c) {
  return {
    key: String(c.id),
    label: `${c.name} (${formatTokenSymbol(c.symbol)})`
  };
}
function _temp2(acc, a_0) {
  acc[a_0.status] = (acc[a_0.status] ?? 0) + 1;
  return acc;
}
function _temp(last) {
  return last.data.page.current_page < last.data.page.last_page ? last.data.page.current_page + 1 : undefined;
}